// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Payment numeric keypad
// Drives usePaymentStore actions. Shows current amount + selected type.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import { usePaymentStore } from '@/store/payment-store';

interface NumPadProps {
  onValidate: () => void;
  showValidateButton?: boolean;
}

const KEYS = [
  ['1', '2', '3', 'C'],
  ['4', '5', '6', 'back'],
  ['7', '8', '9', ','],
  ['00', '0', '.', 'ok'],
] as const;

export default function NumPad({ onValidate, showValidateButton = true }: NumPadProps) {
  const currentAmount = usePaymentStore((s) => s.currentAmount);
  const selectedType = usePaymentStore((s) => s.selectedType);
  const appendDigit = usePaymentStore((s) => s.appendDigit);
  const addDecimal = usePaymentStore((s) => s.addDecimal);
  const backspace = usePaymentStore((s) => s.backspace);
  const clearAmount = usePaymentStore((s) => s.clearAmount);

  function handleKey(key: string) {
    switch (key) {
      case 'C':
        clearAmount();
        break;
      case 'back':
        backspace();
        break;
      case ',':
      case '.':
        addDecimal();
        break;
      case 'ok':
        if (showValidateButton) onValidate();
        break;
      case '00':
        appendDigit('0');
        appendDigit('0');
        break;
      default:
        if (/^\d$/.test(key)) appendDigit(key);
    }
  }

  // Physical keyboard support
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        appendDigit(e.key);
      } else if (e.key === ',' || e.key === '.') {
        e.preventDefault();
        addDecimal();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        backspace();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        clearAmount();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (showValidateButton) onValidate();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [appendDigit, addDecimal, backspace, clearAmount, onValidate, showValidateButton]);

  function getKeyLabel(key: string): string {
    if (key === 'back') return '\u2190';
    if (key === 'ok') return '\u2713';
    return key;
  }

  function getKeyClass(key: string): string {
    const base = 'flex items-center justify-center rounded-lg text-lg font-semibold min-h-[48px] min-w-[60px] transition-colors';
    if (key === 'C') return `${base} bg-warning/20 text-warning`;
    if (key === 'back') return `${base} bg-surface text-gray-300`;
    if (key === 'ok') return `${base} bg-accent text-white`;
    return `${base} bg-surface-card text-white hover:bg-surface-hover`;
  }

  const displayAmount = currentAmount || '0';

  return (
    <div className="space-y-2">
      {/* Amount display */}
      <div className="bg-surface rounded-lg px-4 py-3 text-right">
        <span className="text-2xl font-bold text-white">{displayAmount}</span>
        <span className="text-xl text-gray-400 ml-1">EUR</span>
        {selectedType && (
          <span className="text-xs text-accent-light ml-2 uppercase">{selectedType}</span>
        )}
      </div>

      {/* Keypad grid */}
      <div className="grid grid-cols-4 gap-2">
        {KEYS.flat().map((key, idx) => (
          <button
            key={`${key}-${idx}`}
            type="button"
            onClick={() => handleKey(key)}
            className={getKeyClass(key)}
          >
            {getKeyLabel(key)}
          </button>
        ))}
      </div>
    </div>
  );
}
